/* eslint-disable indent */
import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';
import { formatThousands } from '../../utils/string.utils';
import { PAGE_SIZE_OPTIONS } from '@/hooks/page-size.hooks';

interface PaginationProps {
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  /** When provided, a selector to choose the number of items per page is displayed. */
  pageSize?: number;
  onPageSizeChange?: (pageSize: number) => void;
  /** Total number of items (all pages), used to display "1–20 of 153". */
  totalItems?: number;
}

const Pagination = ({
  totalPages,
  currentPage,
  onPageChange,
  pageSize,
  onPageSizeChange,
  totalItems,
}: PaginationProps) => {
  const { t } = useTranslation();

  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const startPage = Math.max(1, currentPage - 2);
      const endPage = Math.min(totalPages, currentPage + 2);

      if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) {
          pages.push('...');
        }
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          pages.push('...');
        }
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();
  const showPages = totalPages > 1;
  const firstItem = pageSize ? (currentPage - 1) * pageSize + 1 : undefined;
  const lastItem =
    pageSize && totalItems !== undefined
      ? Math.min(currentPage * pageSize, totalItems)
      : undefined;

  return (
    <div className="flex w-full flex-wrap items-center justify-between gap-4 max-sm:justify-center">
      {pageSize && onPageSizeChange ? (
        <div className="flex flex-wrap items-center gap-2.5 text-2sm text-gray-600 dark-mode:text-gray-700">
          <label className="flex items-center gap-2">
            <span>{t('common:show')}</span>
            <select
              className="select select-sm h-8 w-[78px] pr-7 font-medium text-black-071336 bg-white border-gray-DBDFE9 dark-mode:bg-coal-500 dark-mode:text-gray-800"
              value={pageSize}
              aria-label={t('common:itemsPerPage')}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
            >
              {PAGE_SIZE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <span>{t('common:perPage')}</span>
          </label>
          {totalItems !== undefined &&
            totalItems > 0 &&
            firstItem &&
            lastItem && (
              <span className="text-gray-500">
                ·{' '}
                {t('common:itemsRange', {
                  from: formatThousands(firstItem),
                  to: formatThousands(lastItem),
                  total: formatThousands(totalItems),
                })}
              </span>
            )}
        </div>
      ) : (
        <span />
      )}

      {showPages && (
        <div className="pagination justify-center max-sm:w-full">
          <button
            className="btn"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            aria-label={t('common:previousPage')}
          >
            <i className="ki-outline ki-black-left"></i>
          </button>

          {pageNumbers.map((page, index) =>
            page === '...' ? (
              <span key={`page_${index}`} className="btn disabled -mx-2">
                ...
              </span>
            ) : (
              <button
                key={page}
                className={twMerge(
                  'btn !px-2.5 !w-max',
                  page === currentPage && 'active',
                )}
                onClick={() => onPageChange(page)}
              >
                {formatThousands(page)}
              </button>
            ),
          )}

          <button
            className="btn"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            aria-label={t('common:nextPage')}
          >
            <i className="ki-outline ki-black-right"></i>
          </button>
        </div>
      )}
    </div>
  );
};

export default Pagination;
